import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toPng, toJpeg } from "html-to-image";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Download, Plus, Trash2,
  Image as ImageIcon, Type, Palette, Layers as LayersIcon, Grid,
  Square, Circle as CircleIcon, Triangle, Upload,
  ChevronUp, ChevronDown, Copy, Bold, Italic,
  AlignLeft, AlignCenter, AlignRight, ZoomIn, ZoomOut, Maximize2,
  Undo2, Redo2, Youtube, Sparkles, Wand2, Scissors, Loader2, RotateCw,
  AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd,
  Eye, EyeOff, Lock, Unlock, Link as LinkIcon, Replace, ChevronDown as ChevD,
} from "lucide-react";
import { CanvasView } from "@/components/thumbnail/CanvasView";

const FONT_SIZES = [24, 36, 48, 60, 72, 96, 120, 160, 200];
// Default fallback fonts — real catalog loaded from backend
const FONT_FAMILIES = [
  { id: "anton", label: "Anton", css: "'Anton', Impact, sans-serif" },
  { id: "bebas", label: "Bebas Neue", css: "'Bebas Neue', Impact, sans-serif" },
  { id: "poppins", label: "Poppins", css: "'Poppins', system-ui, sans-serif" },
  { id: "inter", label: "Inter", css: "'Inter', system-ui, sans-serif" },
  { id: "montserrat", label: "Montserrat", css: "'Montserrat', system-ui, sans-serif" },
  { id: "oswald", label: "Oswald", css: "'Oswald', Impact, sans-serif" },
  { id: "playfair", label: "Playfair Display", css: "'Playfair Display', Georgia, serif" },
  { id: "archivo", label: "Archivo Black", css: "'Archivo Black', Impact, sans-serif" },
  { id: "bangers", label: "Bangers", css: "'Bangers', Impact, sans-serif" },
  { id: "kalam", label: "Kalam", css: "'Kalam', system-ui, sans-serif" },
  { id: "system", label: "System", css: "system-ui, sans-serif" },
];
const COLOR_SWATCHES = [
  "#FFFFFF", "#000000", "#0B0D17", "#DC2626", "#EA580C",
  "#FBBF24", "#10B981", "#059669", "#0B5CFF", "#7C3AED",
  "#DB2777", "#94A3B8",
];
const DEFAULT_FORMAT = { id: "yt-thumb", label: "YouTube Thumbnail", w: 1280, h: 720, ratio: "16/9", accent: "#DC2626", icon: "youtube" };

const uid = () => Math.random().toString(36).slice(2, 10);

export default function ThumbnailEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [design, setDesign] = useState(null);
  const [format, setFormat] = useState(DEFAULT_FORMAT);
  const [presets, setPresets] = useState([]);
  const [niches, setNiches] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);  // multi-select support
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sidebar, setSidebar] = useState("studio"); // studio | templates | text | elements | uploads | bg | ai
  const [zoom, setZoom] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [tplNiche, setTplNiche] = useState("Motivation");
  const [tplQuery, setTplQuery] = useState("");
  const canvasBoxRef = useRef(null);
  const canvasStageRef = useRef(null);
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const skipHistory = useRef(false);

  // ---------------- Load ----------------
  useEffect(() => {
    (async () => {
      try {
        const [{ data }, { data: p }] = await Promise.all([
          api.get(`/thumbnail-studio/designs/${id}`),
          api.get("/thumbnail-studio/presets"),
        ]);
        setDesign(data);
        setPresets(p.presets);
        setNiches(p.niches);
        setFormat(p.presets.find((x) => x.id === data.format_id) || p.presets[0] || DEFAULT_FORMAT);
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message);
        nav("/app/studio");
      }
    })();
  }, [id, nav]);

  // load templates for left sidebar
  useEffect(() => {
    if (sidebar !== "templates") return;
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (tplNiche && tplNiche !== "All") params.set("niche", tplNiche);
        if (tplQuery.trim()) params.set("q", tplQuery.trim());
        params.set("limit", "40");
        const { data } = await api.get(`/thumbnail-studio/templates?${params}`);
        setTemplates(data.templates);
      } catch (e) { /* noop */ }
    }, 250);
    return () => clearTimeout(t);
  }, [sidebar, tplNiche, tplQuery]);

  const [structuredTemplates, setStructuredTemplates] = useState([]);
  const [fontCatalog, setFontCatalog] = useState(FONT_FAMILIES);

  // Load structured (Canva-style) templates + font catalog once
  useEffect(() => {
    (async () => {
      try {
        const [{ data: st }, { data: fn }] = await Promise.all([
          api.get(`/thumbnail-studio/structured-templates`),
          api.get(`/thumbnail-studio/fonts`),
        ]);
        setStructuredTemplates(st.templates || []);
        if (fn.fonts?.length) setFontCatalog(fn.fonts);
      } catch (e) { /* noop */ }
    })();
  }, []);

  const applyStructuredTemplate = (tpl) => {
    if (!window.confirm(`Load "${tpl.name}" — this will replace current design with editable layers.`)) return;
    setCanvas(() => ({
      bg: JSON.parse(JSON.stringify(tpl.bg || { kind: "color", color: "#FFFFFF" })),
      elements: (tpl.elements || []).map((e) => ({ ...JSON.parse(JSON.stringify(e)), id: uid() })),
    }));
    toast.success(`Loaded ${tpl.name} — all layers editable`);
  };

  // ---------------- History ----------------
  const commitHistory = useCallback((prev) => {
    if (skipHistory.current) return;
    if (!prev) return;
    undoStack.current.push(JSON.stringify(prev.canvas));
    if (undoStack.current.length > 60) undoStack.current.shift();
    redoStack.current = [];
  }, []);

  const setCanvas = useCallback((updater) => {
    setDesign((d) => {
      if (!d) return d;
      commitHistory(d);
      const next = { ...d, canvas: updater(d.canvas || { bg: { kind: "color", color: "#0B5CFF" }, elements: [] }) };
      return next;
    });
    setDirty(true);
  }, [commitHistory]);

  const undo = useCallback(() => {
    if (!undoStack.current.length || !design) return;
    const snap = undoStack.current.pop();
    redoStack.current.push(JSON.stringify(design.canvas));
    skipHistory.current = true;
    setDesign((d) => ({ ...d, canvas: JSON.parse(snap) }));
    skipHistory.current = false;
    setDirty(true);
  }, [design]);

  const redo = useCallback(() => {
    if (!redoStack.current.length || !design) return;
    const snap = redoStack.current.pop();
    undoStack.current.push(JSON.stringify(design.canvas));
    skipHistory.current = true;
    setDesign((d) => ({ ...d, canvas: JSON.parse(snap) }));
    skipHistory.current = false;
    setDirty(true);
  }, [design]);

  // ---------------- Element ops ----------------
  const patchElement = (id, patch) => {
    setCanvas((c) => ({
      ...c,
      elements: (c.elements || []).map((e) => (e.id === id ? { ...e, ...patch } : e)),
    }));
  };
  const deleteElement = (id) => {
    setCanvas((c) => ({ ...c, elements: (c.elements || []).filter((e) => e.id !== id) }));
    setSelectedId((cur) => (cur === id ? null : cur));
  };
  const addElement = (el) => {
    const e = { id: uid(), rotation: 0, opacity: 1, ...el };
    setCanvas((c) => ({ ...c, elements: [...(c.elements || []), e] }));
    setSelectedId(e.id);
  };
  const duplicateElement = (id) => {
    const e = (design?.canvas?.elements || []).find((x) => x.id === id);
    if (!e) return;
    const copy = { ...e, id: uid(), x: (e.x || 0) + 4, y: (e.y || 0) + 4 };
    setCanvas((c) => ({ ...c, elements: [...(c.elements || []), copy] }));
    setSelectedId(copy.id);
  };
  const moveLayer = (id, dir) => {
    setCanvas((c) => {
      const arr = [...(c.elements || [])];
      const i = arr.findIndex((e) => e.id === id);
      if (i < 0) return c;
      const j = dir === "up" ? Math.min(arr.length - 1, i + 1) : Math.max(0, i - 1);
      if (i === j) return c;
      const [item] = arr.splice(i, 1);
      arr.splice(j, 0, item);
      return { ...c, elements: arr };
    });
  };
  const setBg = (patch) => {
    setCanvas((c) => ({ ...c, bg: { ...(c.bg || {}), ...patch } }));
  };

  // ---------- Multi-select + group ops ----------
  const toggleSelect = (id, additive) => {
    if (additive) {
      setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
      setSelectedId(id);
    } else {
      setSelectedIds([id]);
      setSelectedId(id);
    }
  };
  const selectionIds = () => (selectedIds.length ? selectedIds : (selectedId ? [selectedId] : []));
  const rotateSelected = (delta) => {
    const ids = selectionIds(); if (!ids.length) return;
    setCanvas((c) => ({ ...c, elements: (c.elements || []).map((e) => ids.includes(e.id) ? { ...e, rotation: ((e.rotation || 0) + delta) % 360 } : e) }));
  };
  const alignSelected = (mode) => {
    const ids = selectionIds(); if (ids.length < 2) { toast.error("Select 2+ elements"); return; }
    const els = (design.canvas.elements || []).filter((e) => ids.includes(e.id));
    let target = 0;
    if (mode === "left") target = Math.min(...els.map((e) => e.x));
    else if (mode === "right") target = Math.max(...els.map((e) => e.x + e.w));
    else if (mode === "hcenter") target = els.reduce((s, e) => s + (e.x + e.w / 2), 0) / els.length;
    else if (mode === "top") target = Math.min(...els.map((e) => e.y));
    else if (mode === "bottom") target = Math.max(...els.map((e) => e.y + e.h));
    else if (mode === "vcenter") target = els.reduce((s, e) => s + (e.y + e.h / 2), 0) / els.length;
    setCanvas((c) => ({ ...c, elements: (c.elements || []).map((e) => {
      if (!ids.includes(e.id)) return e;
      if (mode === "left")    return { ...e, x: target };
      if (mode === "right")   return { ...e, x: target - e.w };
      if (mode === "hcenter") return { ...e, x: target - e.w / 2 };
      if (mode === "top")     return { ...e, y: target };
      if (mode === "bottom")  return { ...e, y: target - e.h };
      if (mode === "vcenter") return { ...e, y: target - e.h / 2 };
      return e;
    }) }));
  };
  const deleteSelectedAll = () => {
    const ids = selectionIds(); if (!ids.length) return;
    setCanvas((c) => ({ ...c, elements: (c.elements || []).filter((e) => !ids.includes(e.id)) }));
    setSelectedId(null); setSelectedIds([]);
  };

  // ---------------- Add-element presets ----------------
  const addHeading = () => addElement({
    type: "text", text: "BIG HEADLINE", x: 4, y: 20, w: 70,
    size: 140, weight: 900, color: "#FBBF24", bg: null,
    font_family: FONT_FAMILIES[0].css, align: "left",
    stroke_color: "#000000", stroke_width: 2, shadow: true,
  });
  const addSubheading = () => addElement({
    type: "text", text: "Supporting text", x: 4, y: 60, w: 60,
    size: 60, weight: 700, color: "#FFFFFF", bg: "#DC2626",
    font_family: FONT_FAMILIES[0].css, align: "left",
  });
  const addBody = () => addElement({
    type: "text", text: "Description goes here", x: 4, y: 80, w: 50,
    size: 40, weight: 500, color: "#FFFFFF",
    font_family: FONT_FAMILIES[1].css, align: "left",
  });
  const addShape = (shape) => addElement({
    type: "shape", shape, x: 30, y: 30, w: 30, h: 30, fill: "#DC2626", radius: shape === "rect" ? 4 : 0,
  });
  const addImage = (url) => addElement({
    type: "image", x: 30, y: 30, w: 30, h: 30, url,
  });

  // ---------------- Save / Export ----------------
  const save = async () => {
    if (!design) return;
    setSaving(true);
    try {
      let preview_url = design.preview_url;
      try {
        if (canvasStageRef.current) {
          preview_url = await toPng(canvasStageRef.current, { pixelRatio: 1, cacheBust: true, skipFonts: true });
        }
      } catch { /* preview best-effort */ }
      await api.put(`/thumbnail-studio/designs/${id}`, { title: design.title, canvas: design.canvas, preview_url });
      setDirty(false);
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const download = async (fmt = "png", customW = null) => {
    if (!canvasStageRef.current) return;
    try {
      const w = canvasStageRef.current.offsetWidth;
      const targetW = customW || format.w || 1280;
      const options = {
        pixelRatio: targetW / w,
        cacheBust: true,
        skipFonts: true,
        backgroundColor: fmt === "jpg" ? "#000000" : undefined,
      };
      const url = fmt === "jpg"
        ? await toJpeg(canvasStageRef.current, { ...options, quality: 0.92 })
        : fmt === "webp"
          ? await (await import("html-to-image")).toBlob(canvasStageRef.current, { ...options, type: "image/webp", quality: 0.92 }).then((b) => URL.createObjectURL(b))
          : await toPng(canvasStageRef.current, options);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(design.title || "thumbnail").replace(/[^a-z0-9-]+/gi, "-")}-${targetW}.${fmt}`;
      a.click();
      toast.success(`Downloaded ${fmt.toUpperCase()} @ ${targetW}px`);
    } catch (e) { toast.error("Export failed: " + e.message); }
  };
  const [downloadOpen, setDownloadOpen] = useState(false);

  const applyTemplate = async (tpl) => {
    if (!window.confirm("Replace current design with this template?")) return;
    setCanvas((_) => JSON.parse(JSON.stringify(tpl.canvas || { bg: { kind: "color", color: "#0B5CFF" }, elements: [] })));
    const p = presets.find((x) => x.id === tpl.format_id);
    if (p) setFormat(p);
    setDesign((d) => ({ ...d, format_id: tpl.format_id }));
    setSelectedId(null);
  };

  // Fit-to-view zoom
  const fitZoom = () => setZoom(1);

  // Keyboard shortcuts for undo/redo/save
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (mod && (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey))) { e.preventDefault(); redo(); }
      else if (mod && e.key.toLowerCase() === "s") { e.preventDefault(); save(); }
      else if (mod && e.key.toLowerCase() === "d") { e.preventDefault(); if (selectedId) duplicateElement(selectedId); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undo, redo, selectedId, design]);

  const selected = useMemo(
    () => (design?.canvas?.elements || []).find((e) => e.id === selectedId) || null,
    [design, selectedId]
  );

  if (!design) return <div className="text-[#4A4D57] p-6">Loading editor…</div>;

  return (
    <div className="space-y-3 vp-fade-up">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => { if (dirty && !window.confirm("Unsaved changes — leave anyway?")) return; nav("/app/studio"); }}
            className="p-2 rounded-lg hover:bg-[#F1F3F7]"
            aria-label="Back"
            data-testid="editor-back-btn"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <input
            className="vp-input font-display font-semibold text-lg py-1.5 max-w-md"
            value={design.title}
            onChange={(e) => { setDesign({ ...design, title: e.target.value }); setDirty(true); }}
            data-testid="editor-title-input"
          />
          <span className="text-xs text-[#747680] hidden md:inline">{format.label} · {format.w}×{format.h}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={undo} className="p-2 rounded-lg hover:bg-[#F1F3F7]" title="Undo (⌘Z)" data-testid="editor-undo-btn">
            <Undo2 className="w-4 h-4" />
          </button>
          <button onClick={redo} className="p-2 rounded-lg hover:bg-[#F1F3F7]" title="Redo (⌘⇧Z)" data-testid="editor-redo-btn">
            <Redo2 className="w-4 h-4" />
          </button>
          <div className="w-px h-6 bg-[#E4E6EC] mx-1" />
          <button onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)))} className="p-2 rounded-lg hover:bg-[#F1F3F7]" title="Zoom out" data-testid="editor-zoom-out">
            <ZoomOut className="w-4 h-4" />
          </button>
          <button onClick={fitZoom} className="px-2 py-1 text-xs rounded-lg hover:bg-[#F1F3F7]" title="Fit">
            {Math.round(zoom * 100)}%
          </button>
          <button onClick={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)))} className="p-2 rounded-lg hover:bg-[#F1F3F7]" title="Zoom in" data-testid="editor-zoom-in">
            <ZoomIn className="w-4 h-4" />
          </button>
          <div className="w-px h-6 bg-[#E4E6EC] mx-1" />
          <button onClick={save} disabled={saving || !dirty} className="vp-btn-ghost inline-flex items-center gap-1.5 text-sm py-2 px-3 disabled:opacity-50" data-testid="editor-save-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : (dirty ? "Save" : "Saved")}
          </button>
          <div className="relative">
            <button
              onClick={() => setDownloadOpen((v) => !v)}
              className="vp-btn-primary inline-flex items-center gap-1.5 text-sm py-2 px-3"
              data-testid="editor-download-btn"
            >
              <Download className="w-4 h-4" /> Download <ChevD className="w-3 h-3" />
            </button>
            {downloadOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setDownloadOpen(false)} />
                <div className="absolute right-0 top-full mt-1 vp-card bg-white shadow-xl p-2 w-56 z-50 space-y-1" data-testid="download-menu">
                  <div className="vp-label px-1 pt-1">Format & size</div>
                  {[
                    { label: "PNG · 1280×720 (YT)", fmt: "png", w: 1280 },
                    { label: "PNG · 1920×1080 (HD)", fmt: "png", w: 1920 },
                    { label: "JPG · 1280×720", fmt: "jpg", w: 1280 },
                    { label: "JPG · 1920×1080", fmt: "jpg", w: 1920 },
                    { label: "WebP · 1280×720", fmt: "webp", w: 1280 },
                    { label: "Native size", fmt: "png", w: null },
                  ].map((opt) => (
                    <button
                      key={opt.label}
                      onClick={() => { setDownloadOpen(false); download(opt.fmt, opt.w); }}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F1F3F7] text-sm"
                      data-testid={`download-${opt.fmt}-${opt.w || "native"}`}
                    >{opt.label}</button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-[240px_1fr_300px] gap-3 min-h-[560px]">
        {/* LEFT SIDEBAR */}
        <div className="vp-card p-3 space-y-2 h-full max-h-[calc(100vh-140px)] overflow-y-auto">
          <TabButtons
            active={sidebar}
            setActive={setSidebar}
            items={[
              { id: "studio", label: "Studio", icon: Sparkles },
              { id: "templates", label: "YouTube", icon: Grid },
              { id: "text", label: "Text", icon: Type },
              { id: "elements", label: "Elements", icon: Square },
              { id: "uploads", label: "Media", icon: Upload },
              { id: "bg", label: "BG", icon: ImageIcon },
              { id: "ai", label: "AI", icon: Wand2 },
            ]}
          />

          {sidebar === "studio" && (
            <div className="space-y-2" data-testid="studio-templates-panel">
              <div className="text-xs text-[#4A4D57] mb-1">15 Canva-style templates. Click to load all editable layers (fonts, shapes, text) instantly.</div>
              <div className="grid grid-cols-1 gap-2">
                {structuredTemplates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => applyStructuredTemplate(t)}
                    className="relative rounded-md overflow-hidden border border-[#E4E6EC] hover:border-[#0B5CFF] group text-left"
                    title={t.name}
                    data-testid={`studio-tpl-${t.id}`}
                  >
                    <div className="aspect-video">
                      <TemplateThumbnail template={t} />
                    </div>
                    <div className="p-1.5">
                      <div className="text-[11px] font-semibold truncate">{t.name}</div>
                      <div className="text-[9px] text-[#747680] flex justify-between">
                        <span>{t.niche}</span>
                        <span>{t.layer_count} layers</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {sidebar === "templates" && (
            <div className="space-y-2">
              <input
                className="vp-input text-xs"
                placeholder="Search…"
                value={tplQuery}
                onChange={(e) => setTplQuery(e.target.value)}
                data-testid="editor-tpl-search"
              />
              <select
                className="vp-input text-xs"
                value={tplNiche}
                onChange={(e) => setTplNiche(e.target.value)}
                data-testid="editor-tpl-niche"
              >
                <option>All</option>
                {niches.map((n) => <option key={n}>{n}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t)}
                    className="relative rounded-md overflow-hidden border border-[#E4E6EC] hover:border-[#0B5CFF] group"
                    title={t.title}
                    data-testid={`editor-tpl-${t.id}`}
                  >
                    <div style={{ aspectRatio: (t.format_id === "yt-short" || t.format_id === "ig-story") ? "9/16" : "16/9" }}>
                      <img
                        src={t.thumbnail_url}
                        alt=""
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-cover"
                        onError={(e) => { if (e.currentTarget.src.includes("maxresdefault")) e.currentTarget.src = e.currentTarget.src.replace("maxresdefault", "hqdefault"); }}
                      />
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-1.5 py-1">
                      <div className="text-[10px] text-white truncate">{t.title}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {sidebar === "text" && (
            <div className="space-y-2">
              <button onClick={addHeading} className="w-full text-left px-3 py-3 rounded-lg border border-[#E4E6EC] hover:border-[#0B5CFF]" data-testid="editor-add-heading">
                <div className="font-black text-lg leading-none">Add heading</div>
                <div className="text-xs text-[#747680] mt-1">Bold display text</div>
              </button>
              <button onClick={addSubheading} className="w-full text-left px-3 py-3 rounded-lg border border-[#E4E6EC] hover:border-[#0B5CFF]" data-testid="editor-add-subheading">
                <div className="font-bold text-base leading-none">Add subheading</div>
                <div className="text-xs text-[#747680] mt-1">Medium supporting text</div>
              </button>
              <button onClick={addBody} className="w-full text-left px-3 py-3 rounded-lg border border-[#E4E6EC] hover:border-[#0B5CFF]" data-testid="editor-add-body">
                <div className="text-sm">Add body text</div>
              </button>
            </div>
          )}

          {sidebar === "elements" && (
            <div className="space-y-2">
              <div className="text-xs font-semibold text-[#4A4D57]">Shapes</div>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => addShape("rect")} className="aspect-square rounded-lg border border-[#E4E6EC] hover:border-[#0B5CFF] flex items-center justify-center" data-testid="editor-add-rect">
                  <Square className="w-6 h-6" fill="#DC2626" strokeWidth={0} />
                </button>
                <button onClick={() => addShape("circle")} className="aspect-square rounded-lg border border-[#E4E6EC] hover:border-[#0B5CFF] flex items-center justify-center" data-testid="editor-add-circle">
                  <CircleIcon className="w-6 h-6" fill="#FBBF24" strokeWidth={0} />
                </button>
                <button onClick={() => addShape("triangle")} className="aspect-square rounded-lg border border-[#E4E6EC] hover:border-[#0B5CFF] flex items-center justify-center" data-testid="editor-add-triangle">
                  <Triangle className="w-6 h-6" fill="#059669" strokeWidth={0} />
                </button>
              </div>
              <div className="text-xs font-semibold text-[#4A4D57] mt-3">Emoji stickers</div>
              <div className="grid grid-cols-6 gap-1 text-2xl">
                {["🔥","💯","😱","💰","⚡","🎯","🚀","👀","✅","❌","💡","🏆"].map((em) => (
                  <button
                    key={em}
                    onClick={() => addElement({ type: "text", text: em, x: 40, y: 40, w: 15, size: 160, weight: 400, color: "#000", font_family: "system-ui" })}
                    className="rounded-lg border border-[#E4E6EC] hover:border-[#0B5CFF] py-1"
                  >
                    {em}
                  </button>
                ))}
              </div>
            </div>
          )}

          {sidebar === "uploads" && (
            <MediaTab addImage={addImage} />
          )}

          {sidebar === "bg" && (
            <div className="space-y-3">
              <div className="text-xs font-semibold">Background colour</div>
              <div className="grid grid-cols-6 gap-1.5">
                {COLOR_SWATCHES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setBg({ kind: "color", color: c, url: undefined })}
                    className="w-7 h-7 rounded-full border-2 border-white shadow-sm"
                    style={{ background: c }}
                    aria-label={c}
                  />
                ))}
              </div>
              <div className="text-xs font-semibold mt-3">Image URL</div>
              <input
                className="vp-input text-xs"
                placeholder="https://…"
                value={design.canvas?.bg?.url || ""}
                onChange={(e) => setBg({ kind: "image", url: e.target.value })}
                data-testid="editor-bg-url"
              />
              {design.canvas?.bg?.kind === "image" && design.canvas?.bg?.url && (
                <button
                  onClick={() => {
                    // detach BG image to a movable layer at 0,0 covering the canvas
                    const url = design.canvas.bg.url;
                    addElement({ type: "image", x: 0, y: 0, w: 100, h: 100, url, fit: "cover" });
                    setBg({ kind: "color", color: "#FFFFFF", url: undefined });
                    toast.success("Background detached — now a movable layer");
                  }}
                  className="vp-btn-ghost text-xs py-1.5 px-2 w-full inline-flex items-center justify-center gap-1.5"
                  data-testid="detach-bg-btn"
                >
                  <Scissors className="w-3.5 h-3.5" /> Detach BG to layer
                </button>
              )}
              <div className="text-[11px] text-[#747680]">
                Tip: Studio tab has 15 Canva-style templates with editable layers.
              </div>
            </div>
          )}

          {sidebar === "ai" && (
            <AIPanel
              canvas={design.canvas}
              setCanvas={setCanvas}
              addElement={addElement}
              onOneClickCreated={(newDesign) => {
                nav(`/app/studio/${newDesign.id}/edit`);
                toast.success("New AI thumbnail ready ✨");
              }}
            />
          )}
        </div>

        {/* CENTER CANVAS */}
        <div ref={canvasBoxRef}
          className="rounded-xl bg-[#0B0D17] relative overflow-auto flex items-center justify-center p-6"
          style={{ minHeight: 500 }}
        >
          <div
            style={{ transform: `scale(${zoom})`, transformOrigin: "center center", transition: "transform 0.15s" }}
          >
            <div
              ref={canvasStageRef}
              className="shadow-2xl rounded-md overflow-hidden bg-black"
              style={{
                width: format.ratio === "9/16" ? 360 : (format.ratio === "1/1" ? 560 : 800),
              }}
            >
              <CanvasView
                canvas={design.canvas}
                format={format}
                editable
                selectedId={selectedId}
                onElementChange={patchElement}
                onElementSelect={setSelectedId}
                onElementDelete={deleteElement}
              />
            </div>
          </div>
        </div>

        {/* RIGHT PANEL - Layers + Properties */}
        <div className="vp-card p-3 h-full max-h-[calc(100vh-140px)] overflow-y-auto space-y-3">
          {/* LAYERS */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-display font-semibold text-sm inline-flex items-center gap-1.5">
                <LayersIcon className="w-4 h-4" /> Layers
              </div>
              <span className="text-[10px] text-[#747680]">{(design.canvas?.elements || []).length}</span>
            </div>
            <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
              {[...(design.canvas?.elements || [])].reverse().map((el) => (
                <LayerItem
                  key={el.id}
                  el={el}
                  active={selectedId === el.id || selectedIds.includes(el.id)}
                  onSelect={(evt) => toggleSelect(el.id, evt?.shiftKey)}
                  onRename={(name) => patchElement(el.id, { name })}
                  onToggleHide={() => patchElement(el.id, { hidden: !el.hidden })}
                  onToggleLock={() => patchElement(el.id, { locked: !el.locked })}
                  onMoveUp={() => moveLayer(el.id, "up")}
                  onMoveDown={() => moveLayer(el.id, "down")}
                  onDuplicate={() => duplicateElement(el.id)}
                  onDelete={() => deleteElement(el.id)}
                />
              ))}
              {(design.canvas?.elements || []).length === 0 && (
                <div className="text-xs text-[#747680] py-3 text-center">
                  No layers yet. Add text or elements from the left.
                </div>
              )}
            </div>
          </div>

          {/* PROPERTIES */}
          {selected && (
            <div data-testid="editor-properties">
              <div className="font-display font-semibold text-sm mb-2 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5"><Palette className="w-4 h-4" /> Properties {selectedIds.length > 1 ? `(${selectedIds.length})` : ""}</span>
              </div>
              {/* Group ops bar */}
              <div className="flex items-center gap-1 mb-2 flex-wrap" data-testid="thumb-ops-bar">
                <button onClick={() => rotateSelected(90)} className="vp-btn-ghost text-xs py-1 px-1.5 inline-flex items-center gap-1" title="Rotate 90°" data-testid="thumb-rotate-btn"><RotateCw className="w-3.5 h-3.5" /></button>
                <button onClick={() => rotateSelected(-15)} className="vp-btn-ghost text-xs py-1 px-1.5" title="Rotate -15°">-15°</button>
                {selectedIds.length >= 2 && (
                  <>
                    <div className="w-px h-4 bg-[#E4E6EC]" />
                    <button onClick={() => alignSelected("left")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Align left"><AlignLeft className="w-3.5 h-3.5" /></button>
                    <button onClick={() => alignSelected("hcenter")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Align h-center"><AlignCenter className="w-3.5 h-3.5" /></button>
                    <button onClick={() => alignSelected("right")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Align right"><AlignRight className="w-3.5 h-3.5" /></button>
                    <button onClick={() => alignSelected("top")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Align top"><AlignVerticalJustifyStart className="w-3.5 h-3.5" /></button>
                    <button onClick={() => alignSelected("vcenter")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Align v-center"><AlignVerticalJustifyCenter className="w-3.5 h-3.5" /></button>
                    <button onClick={() => alignSelected("bottom")} className="vp-btn-ghost text-xs py-1 px-1.5" title="Align bottom"><AlignVerticalJustifyEnd className="w-3.5 h-3.5" /></button>
                    <button onClick={deleteSelectedAll} className="vp-btn-ghost text-xs py-1 px-1.5 text-[#B00020] ml-auto" title="Delete all"><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
              {selected.type === "text" && (
                <TextProps selected={selected} onPatch={(p) => patchElement(selected.id, p)} fontCatalog={fontCatalog} />
              )}
              {selected.type === "image" && (
                <ImageProps selected={selected} onPatch={(p) => patchElement(selected.id, p)} />
              )}
              {selected.type === "shape" && (
                <ShapeProps selected={selected} onPatch={(p) => patchElement(selected.id, p)} />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="text-center text-xs text-[#747680]">
        Tip: Double-click text to edit inline · Drag SE corner to resize · ⌘Z / ⌘⇧Z for undo · Del to remove selected
      </div>
    </div>
  );
}

// ---------------- Subcomponents ----------------
function TabButtons({ active, setActive, items }) {
  return (
    <div className="grid grid-cols-5 gap-1 bg-[#F1F3F7] rounded-lg p-1">
      {items.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          onClick={() => setActive(id)}
          className={"flex flex-col items-center gap-0.5 py-1.5 rounded-md text-[10px] font-medium " + (active === id ? "bg-white shadow-sm text-[#0B0D17]" : "text-[#4A4D57] hover:text-[#0B0D17]")}
          data-testid={`editor-tab-${id}`}
        >
          <Icon className="w-4 h-4" />
          {label}
        </button>
      ))}
    </div>
  );
}

function UrlAdd({ onAdd }) {
  const [u, setU] = useState("");
  return (
    <div className="flex gap-1">
      <input className="vp-input text-xs" placeholder="https://…" value={u} onChange={(e) => setU(e.target.value)} />
      <button
        onClick={() => { if (u.trim()) { onAdd(u.trim()); setU(""); } }}
        className="vp-btn-ghost text-xs px-2"
      >Add</button>
    </div>
  );
}

function TextProps({ selected, onPatch, fontCatalog = FONT_FAMILIES }) {
  const fonts = fontCatalog || FONT_FAMILIES;
  return (
    <div className="space-y-2 text-xs">
      <div>
        <div className="vp-label mb-1">Text</div>
        <textarea
          className="vp-input text-sm"
          rows={2}
          value={selected.text || ""}
          onChange={(e) => onPatch({ text: e.target.value })}
          data-testid="prop-text"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="vp-label mb-1">Size</div>
          <input type="number" min="8" max="600" step="4" className="vp-input py-1 text-sm"
            value={selected.font_size || selected.size || 60}
            onChange={(e) => onPatch({ font_size: parseInt(e.target.value), size: parseInt(e.target.value) })} />
        </div>
        <div>
          <div className="vp-label mb-1">Weight</div>
          <select className="vp-input py-1 text-sm"
            value={selected.font_weight || selected.weight || 900}
            onChange={(e) => onPatch({ font_weight: parseInt(e.target.value), weight: parseInt(e.target.value) })}>
            {[400, 500, 600, 700, 800, 900].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
      </div>
      <div>
        <div className="vp-label mb-1">Font family</div>
        <select
          className="vp-input py-1 text-sm"
          value={fonts.find((f) => f.css === selected.font_family)?.id || "inter"}
          onChange={(e) => onPatch({ font_family: fonts.find((f) => f.id === e.target.value)?.css })}
          data-testid="text-font-family"
        >
          {fonts.map((f) => <option key={f.id} value={f.id} style={{ fontFamily: f.css }}>{f.label}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onPatch({ font_weight: (selected.font_weight || selected.weight || 900) >= 700 ? 500 : 900 })} className={"p-1.5 rounded " + ((selected.font_weight || selected.weight || 900) >= 700 ? "bg-[#0B5CFF] text-white" : "hover:bg-[#F1F3F7]")}><Bold className="w-3.5 h-3.5" /></button>
        <button onClick={() => onPatch({ italic: !selected.italic })} className={"p-1.5 rounded " + (selected.italic ? "bg-[#0B5CFF] text-white" : "hover:bg-[#F1F3F7]")}><Italic className="w-3.5 h-3.5" /></button>
        <div className="w-px h-5 bg-[#E4E6EC] mx-1" />
        {[["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]].map(([a, Ic]) => (
          <button key={a} onClick={() => onPatch({ align: a })} className={"p-1.5 rounded " + (selected.align === a ? "bg-[#0B5CFF] text-white" : "hover:bg-[#F1F3F7]")}>
            <Ic className="w-3.5 h-3.5" />
          </button>
        ))}
      </div>
      <ColorPicker label="Text color" value={selected.color} onChange={(c) => onPatch({ color: c })} />
      <ColorPicker label="Highlight bg" value={selected.bg} allowNull onChange={(c) => onPatch({ bg: c })} />
      <div className="grid grid-cols-2 gap-2">
        <NumberRow label="Letter spacing" value={selected.letter_spacing || 0} min={-10} max={30} step={0.5} onChange={(v) => onPatch({ letter_spacing: v })} />
        <NumberRow label="Line height" value={selected.line_height || 1} min={0.6} max={3} step={0.05} onChange={(v) => onPatch({ line_height: v })} />
      </div>
      {/* Stroke */}
      <div className="border-t border-[#E4E6EC] pt-2 space-y-1">
        <div className="vp-label">Text stroke</div>
        <div className="grid grid-cols-2 gap-2">
          <NumberRow label="Width" value={selected.stroke_width || 0} min={0} max={10} step={0.5} onChange={(v) => onPatch({ stroke_width: v })} />
          <ColorPicker label="Color" value={selected.stroke_color || selected.stroke || "#000000"} onChange={(c) => onPatch({ stroke_color: c, stroke: c })} />
        </div>
      </div>
      {/* Shadow */}
      <div className="border-t border-[#E4E6EC] pt-2 space-y-1">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!!selected.shadow} onChange={(e) => onPatch({ shadow: e.target.checked })} data-testid="text-shadow-toggle" />
          Drop shadow
        </label>
        {selected.shadow && (
          <div className="grid grid-cols-2 gap-2">
            <NumberRow label="Blur" value={selected.shadow_blur ?? 12} min={0} max={40} onChange={(v) => onPatch({ shadow_blur: v })} />
            <NumberRow label="Offset" value={selected.shadow_offset ?? 4} min={0} max={30} onChange={(v) => onPatch({ shadow_offset: v })} />
          </div>
        )}
      </div>
      <NumberRow label="Rotation" value={selected.rotation || 0} min={-180} max={180} onChange={(v) => onPatch({ rotation: v })} />
      <NumberRow label="Opacity" value={Math.round((selected.opacity ?? 1) * 100)} min={0} max={100} onChange={(v) => onPatch({ opacity: v / 100 })} />
    </div>
  );
}

function ImageProps({ selected, onPatch }) {
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const fileRef = useRef(null);
  const doUpload = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    const reader = new FileReader();
    reader.onload = () => { onPatch({ url: reader.result }); setReplaceOpen(false); toast.success("Image replaced"); };
    reader.readAsDataURL(f);
  };
  const doUrl = () => {
    if (!urlInput.trim()) return;
    onPatch({ url: urlInput.trim() }); setReplaceOpen(false); setUrlInput(""); toast.success("Image replaced");
  };
  return (
    <div className="space-y-2 text-xs">
      {/* IMAGE PREVIEW + REPLACE (primary action) */}
      <div className="relative rounded-lg overflow-hidden bg-[#F1F3F7] border border-[#E4E6EC]" style={{ aspectRatio: "16/9" }}>
        {selected.url ? (
          <img src={selected.url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#747680] text-xs">No image</div>
        )}
        <button
          onClick={() => setReplaceOpen(true)}
          className="absolute inset-0 bg-black/0 hover:bg-black/50 transition-colors flex items-center justify-center opacity-0 hover:opacity-100 text-white font-semibold text-xs"
          data-testid="prop-image-replace"
        >
          <Replace className="w-4 h-4 mr-1" /> Replace image
        </button>
      </div>
      <button
        onClick={() => setReplaceOpen(true)}
        className="vp-btn-primary w-full inline-flex items-center justify-center gap-1.5 text-xs py-2"
        data-testid="prop-image-replace-btn"
      >
        <Replace className="w-3.5 h-3.5" /> Replace image
      </button>

      {replaceOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setReplaceOpen(false)}>
          <div className="vp-card bg-white p-4 max-w-sm w-full space-y-3" onClick={(e) => e.stopPropagation()} data-testid="replace-image-modal">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold">Replace image</div>
              <button onClick={() => setReplaceOpen(false)} className="text-[#747680]"><ChevD className="w-4 h-4 rotate-90" /></button>
            </div>
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-[#E4E6EC] hover:border-[#0B5CFF] rounded-lg p-4 text-sm inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="replace-upload-btn"
            >
              <Upload className="w-4 h-4" /> Upload from computer
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} />
            <div className="text-center text-xs text-[#747680]">or</div>
            <div>
              <div className="vp-label mb-1">Paste image URL</div>
              <div className="flex gap-1">
                <input className="vp-input flex-1 text-sm" placeholder="https://…" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} data-testid="replace-url-input" />
                <button onClick={doUrl} className="vp-btn-primary text-xs px-3" data-testid="replace-url-btn">Use</button>
              </div>
            </div>
            <div className="text-[10px] text-[#747680]">Position, size, and effects will be preserved.</div>
          </div>
        </div>
      )}

      {/* Inline URL edit for power users */}
      <details className="text-xs">
        <summary className="cursor-pointer text-[#747680] hover:text-[#0B0D17]">Advanced: URL</summary>
        <input className="vp-input py-1 text-sm mt-1" value={selected.url || ""} onChange={(e) => onPatch({ url: e.target.value })} />
      </details>

      <NumberRow label="Corner radius %" value={selected.radius || 0} min={0} max={50} onChange={(v) => onPatch({ radius: v })} />
      <NumberRow label="Rotation" value={selected.rotation || 0} min={-180} max={180} onChange={(v) => onPatch({ rotation: v })} />
      <NumberRow label="Opacity" value={Math.round((selected.opacity ?? 1) * 100)} min={0} max={100} onChange={(v) => onPatch({ opacity: v / 100 })} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function LayerItem({ el, active, onSelect, onRename, onToggleHide, onToggleLock, onMoveUp, onMoveDown, onDuplicate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const inputRef = useRef(null);
  const commit = () => {
    if (nameDraft.trim() && nameDraft.trim() !== el.name) onRename(nameDraft.trim());
    setEditing(false);
  };
  useEffect(() => { if (editing && inputRef.current) inputRef.current.select(); }, [editing]);

  const defaultLabel = el.type === "text" ? (el.text || "Text") : el.type === "image" ? "Image" : (el.shape || "Shape");
  const label = el.name || defaultLabel;
  const icon = el.type === "text" ? "T" : el.type === "image" ? "🖼" : "◆";

  return (
    <div
      onClick={(e) => onSelect?.(e)}
      className={"flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer text-xs " + (active ? "bg-[#0B5CFF]/10 text-[#0B5CFF]" : "hover:bg-[#F1F3F7]") + (el.hidden ? " opacity-50" : "")}
      data-testid={`layer-item-${el.id}`}
    >
      <span className="w-4 text-center opacity-60">{icon}</span>
      {editing ? (
        <input
          ref={inputRef}
          className="flex-1 min-w-0 bg-white px-1 border border-[#E4E6EC] rounded text-xs"
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span
          className="truncate flex-1"
          onDoubleClick={(e) => { e.stopPropagation(); setNameDraft(label); setEditing(true); }}
          title="Double-click to rename"
        >{label}</span>
      )}
      <button onClick={(e) => { e.stopPropagation(); onToggleHide(); }} className="p-0.5 hover:text-[#0B5CFF]" title={el.hidden ? "Show" : "Hide"} data-testid={`layer-hide-${el.id}`}>
        {el.hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
      </button>
      <button onClick={(e) => { e.stopPropagation(); onToggleLock(); }} className="p-0.5 hover:text-[#0B5CFF]" title={el.locked ? "Unlock" : "Lock"} data-testid={`layer-lock-${el.id}`}>
        {el.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3 opacity-40" />}
      </button>
      <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} className="p-0.5 hover:text-[#0B5CFF]" title="Bring forward"><ChevronUp className="w-3 h-3" /></button>
      <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} className="p-0.5 hover:text-[#0B5CFF]" title="Send backward"><ChevronDown className="w-3 h-3" /></button>
      <button onClick={(e) => { e.stopPropagation(); onDuplicate(); }} className="p-0.5 hover:text-[#0B5CFF]" title="Duplicate"><Copy className="w-3 h-3" /></button>
      <button onClick={(e) => { e.stopPropagation(); if (!el.locked) onDelete(); }} className={"p-0.5 " + (el.locked ? "text-[#E4E6EC] cursor-not-allowed" : "hover:text-[#B00020]")} title={el.locked ? "Unlock first" : "Delete"}><Trash2 className="w-3 h-3" /></button>
    </div>
  );
}

function ShapeProps({ selected, onPatch }) {
  const fillKind = selected.fill_kind || "solid";
  return (
    <div className="space-y-3 text-xs">
      {/* Fill tabs: Solid / Gradient / Image / None */}
      <div>
        <div className="vp-label mb-1">Fill</div>
        <div className="grid grid-cols-4 gap-1 mb-2">
          {[["solid", "Solid"], ["gradient", "Gradient"], ["image", "Image"], ["none", "None"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => onPatch({ fill_kind: k })}
              className={"py-1 text-[10px] rounded border " + (fillKind === k ? "border-[#0B5CFF] bg-[#0B5CFF]/10 text-[#0B5CFF]" : "border-[#E4E6EC] hover:border-[#0B5CFF]/50")}
              data-testid={`fill-tab-${k}`}
            >{label}</button>
          ))}
        </div>
        {fillKind === "solid" && (
          <ColorPicker label="" value={selected.fill} onChange={(c) => onPatch({ fill: c })} />
        )}
        {fillKind === "gradient" && (
          <div className="space-y-1.5">
            <ColorPicker label="From" value={selected.gradient_from || "#7C3AED"} onChange={(c) => onPatch({ gradient_from: c })} />
            <ColorPicker label="To" value={selected.gradient_to || "#0B5CFF"} onChange={(c) => onPatch({ gradient_to: c })} />
            <NumberRow label="Angle°" value={selected.gradient_angle ?? 135} min={0} max={360} onChange={(v) => onPatch({ gradient_angle: v })} />
          </div>
        )}
        {fillKind === "image" && (
          <input
            className="vp-input text-xs" placeholder="https://image.url"
            value={selected.fill_image || ""}
            onChange={(e) => onPatch({ fill_image: e.target.value })}
            data-testid="shape-fill-image"
          />
        )}
      </div>
      {selected.shape !== "circle" && selected.shape !== "triangle" && (
        <div className="grid grid-cols-2 gap-2">
          <NumberRow label="Radius X" value={selected.radius || 0} min={0} max={100} onChange={(v) => onPatch({ radius: v })} />
          <NumberRow label="Radius Y" value={selected.radius_y ?? selected.radius ?? 0} min={0} max={100} onChange={(v) => onPatch({ radius_y: v })} />
        </div>
      )}
      {/* Stroke */}
      <div className="border-t border-[#E4E6EC] pt-2 space-y-1.5">
        <div className="vp-label">Stroke</div>
        <ColorPicker label="" value={selected.stroke || null} allowNull onChange={(c) => onPatch({ stroke: c })} />
        <div className="grid grid-cols-2 gap-2">
          <NumberRow label="Width" value={selected.stroke_width || 0} min={0} max={20} onChange={(v) => onPatch({ stroke_width: v })} />
          <NumberRow label="Dash" value={selected.dash || 0} min={0} max={40} onChange={(v) => onPatch({ dash: v })} />
        </div>
      </div>
      {/* Effects: shadow + blur + glow */}
      <div className="border-t border-[#E4E6EC] pt-2 space-y-1.5">
        <div className="vp-label">Effects</div>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!!selected.shadow} onChange={(e) => onPatch({ shadow: e.target.checked })} data-testid="shape-shadow-toggle" />
          Drop shadow
        </label>
        {selected.shadow && (
          <div className="grid grid-cols-2 gap-2">
            <NumberRow label="Blur" value={selected.shadow_blur ?? 12} min={0} max={40} onChange={(v) => onPatch({ shadow_blur: v })} />
            <NumberRow label="Offset" value={selected.shadow_offset ?? 4} min={0} max={30} onChange={(v) => onPatch({ shadow_offset: v })} />
          </div>
        )}
        <NumberRow label="Blur (element)" value={selected.blur || 0} min={0} max={20} onChange={(v) => onPatch({ blur: v })} />
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!!selected.glow} onChange={(e) => onPatch({ glow: e.target.checked })} />
          Glow (uses fill color)
        </label>
      </div>
      <NumberRow label="Rotation" value={selected.rotation || 0} min={-180} max={180} onChange={(v) => onPatch({ rotation: v })} />
      <NumberRow label="Opacity" value={Math.round((selected.opacity ?? 1) * 100)} min={0} max={100} onChange={(v) => onPatch({ opacity: v / 100 })} />
    </div>
  );
}

function ColorPicker({ label, value, allowNull, onChange }) {
  return (
    <div>
      <div className="vp-label mb-1">{label}</div>
      <div className="flex gap-1 flex-wrap items-center">
        {allowNull && (
          <button onClick={() => onChange(null)} className={"w-5 h-5 rounded-full border-2 " + (!value ? "border-[#0B5CFF]" : "border-white shadow-sm") + " bg-white text-[10px]"}>∅</button>
        )}
        {COLOR_SWATCHES.map((c) => (
          <button key={c} onClick={() => onChange(c)}
            className={"w-5 h-5 rounded-full border-2 " + (value === c ? "border-[#0B5CFF]" : "border-white shadow-sm")}
            style={{ background: c }} aria-label={c}
          />
        ))}
        <input
          type="color"
          value={value && value.startsWith("#") ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-5 h-5 rounded"
        />
      </div>
    </div>
  );
}

function NumberRow({ label, value, min, max, step, onChange }) {
  const s = step ?? 1;
  return (
    <div>
      {label ? <div className="vp-label mb-1">{label}</div> : null}
      <input type="number" step={s} min={min} max={max} className="vp-input py-1 text-sm" value={value}
             onChange={(e) => onChange(parseFloat(e.target.value) || 0)} />
    </div>
  );
}

// --------------------------------------------------------------
// TemplateThumbnail — miniature render of a structured template
// --------------------------------------------------------------
function TemplateThumbnail({ template }) {
  const bg = template.bg || { kind: "color", color: "#FFF" };
  const bgStyle = bg.kind === "image"
    ? { backgroundImage: `url(${bg.url})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { background: bg.color || "#FFF" };
  return (
    <div className="relative w-full h-full overflow-hidden" style={bgStyle}>
      {(template.elements || []).map((el) => {
        const style = {
          position: "absolute",
          left: `${el.x}%`, top: `${el.y}%`,
          width: `${el.w}%`, height: `${el.h}%`,
          transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
          opacity: el.opacity ?? 1,
          pointerEvents: "none",
        };
        if (el.type === "text") {
          return (
            <div key={el.id} style={{
              ...style, color: el.color, fontFamily: el.font_family,
              fontWeight: el.font_weight || 700,
              fontSize: `${(el.font_size || 60) / 10}px`,  // scaled for thumbnail
              textAlign: el.align || "left",
              letterSpacing: el.letter_spacing ? `${el.letter_spacing / 10}px` : undefined,
              lineHeight: el.line_height || 1,
              WebkitTextStroke: el.stroke ? `${(el.stroke_width || 2) / 10}px ${el.stroke}` : undefined,
              overflow: "hidden",
              whiteSpace: "pre",
            }}>{el.text}</div>
          );
        }
        if (el.type === "shape") {
          const fillStyle = el.fill_kind === "gradient"
            ? { background: `linear-gradient(${el.gradient_angle || 135}deg, ${el.gradient_from}, ${el.gradient_to})` }
            : { background: el.fill };
          return (
            <div key={el.id} style={{
              ...style, ...fillStyle,
              borderRadius: el.shape === "circle" ? "50%" :
                            el.radius ? `${el.radius}px` : 0,
              border: el.stroke ? `${(el.stroke_width || 1) / 2}px solid ${el.stroke}` : undefined,
            }} />
          );
        }
        if (el.type === "image" && el.url) {
          return <img key={el.id} src={el.url} alt="" style={{ ...style, objectFit: el.fit || "cover" }} />;
        }
        return null;
      })}
    </div>
  );
}

// --------------------------------------------------------------
// AIPanel — One-Click generate + AI text suggestions + Remove BG
// --------------------------------------------------------------
function AIPanel({ canvas, setCanvas, addElement, onOneClickCreated }) {
  const [topic, setTopic] = useState("");
  const [titles, setTitles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [bgBusy, setBgBusy] = useState(false);
  const [imgUrl, setImgUrl] = useState("");
  // One-click state
  const [ocTopic, setOcTopic] = useState("");
  const [ocStyle, setOcStyle] = useState("bold cinematic");
  const [ocNiche, setOcNiche] = useState("");
  const [ocBusy, setOcBusy] = useState(false);
  const [ocVariants, setOcVariants] = useState(null);
  const [ocPicking, setOcPicking] = useState(false);

  const runOneClick = async () => {
    if (ocTopic.trim().length < 3) { toast.error("Enter a topic (min 3 chars)"); return; }
    setOcBusy(true);
    try {
      toast.message("Generating 3 variants + AI background… (~30-45s)");
      const { data } = await api.post("/thumbnail-studio/ai/one-click-variants", {
        topic: ocTopic.trim(),
        niche: ocNiche.trim() || null,
        style: ocStyle,
        language: "English",
        format_id: "yt-thumb",
      });
      setOcVariants(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "One-click AI failed. Please retry.");
    } finally { setOcBusy(false); }
  };

  const pickVariant = async (variant) => {
    setOcPicking(true);
    try {
      const { data } = await api.post("/thumbnail-studio/designs", {
        title: variant.title, format_id: "yt-thumb", canvas: variant.canvas,
      });
      if (onOneClickCreated) onOneClickCreated(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setOcPicking(false); }
  };

  const suggest = async () => {
    if (topic.trim().length < 3) { toast.error("Enter a topic"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/thumbnail-studio/ai/text-suggest",
        { topic, style: "punchy YouTube thumbnail title", language: "English", count: 5 });
      setTitles(data.titles || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const applyTitle = (t) => {
    addElement({ type: "text", text: t.toUpperCase(), x: 4, y: 20, w: 70, h: 25,
      font_family: "'Anton', Impact, sans-serif", font_weight: 900, font_size: 130, color: "#FFFFFF",
      align: "left", letter_spacing: -1, stroke: "#000", stroke_width: 3 });
    toast.success("Title added to canvas");
  };
  const removeBg = async () => {
    const target = imgUrl.trim() || canvas?.bg?.url;
    if (!target) { toast.error("Enter an image URL or set an image background"); return; }
    setBgBusy(true);
    try {
      toast.message("AI is removing background… (~30s)");
      const { data } = await api.post("/thumbnail-studio/ai/remove-bg", { image_url: target });
      const dataUrl = `data:image/png;base64,${data.image_b64}`;
      addElement({ type: "image", x: 15, y: 10, w: 70, h: 80, url: dataUrl, fit: "contain" });
      toast.success("Background removed — added as layer");
    } catch (e) { toast.error(e.response?.data?.detail || "Remove BG failed"); }
    finally { setBgBusy(false); }
  };

  return (
    <div className="space-y-3">
      {/* ONE-CLICK AI THUMBNAIL */}
      <div className="rounded-lg border border-[#7C3AED]/40 bg-gradient-to-br from-[#7C3AED]/8 to-[#0B5CFF]/5 p-3 space-y-2" data-testid="one-click-panel">
        <div className="text-xs font-bold flex items-center gap-1.5 text-[#7C3AED]">
          <Sparkles className="w-3.5 h-3.5" /> One-Click AI Thumbnail
        </div>
        <div className="text-[10px] text-[#4A4D57] leading-snug">
          Topic likho → AI title + Nano Banana background + auto-layout. Editable ready-to-download thumbnail in one shot.
        </div>
        <input
          className="vp-input text-xs"
          placeholder="Topic (e.g. Zerodha vs Groww)"
          value={ocTopic}
          onChange={(e) => setOcTopic(e.target.value)}
          data-testid="one-click-topic"
        />
        <div className="grid grid-cols-2 gap-1.5">
          <select
            className="vp-input text-xs"
            value={ocStyle}
            onChange={(e) => setOcStyle(e.target.value)}
            data-testid="one-click-style"
          >
            <option value="bold cinematic">Bold cinematic</option>
            <option value="minimal editorial">Minimal editorial</option>
            <option value="neon tech">Neon tech</option>
            <option value="pastel finance">Pastel finance</option>
          </select>
          <input
            className="vp-input text-xs"
            placeholder="Niche (optional)"
            value={ocNiche}
            onChange={(e) => setOcNiche(e.target.value)}
            data-testid="one-click-niche"
          />
        </div>
        <button
          onClick={runOneClick}
          disabled={ocBusy}
          className="w-full text-xs py-2 px-3 rounded-md bg-gradient-to-r from-[#7C3AED] to-[#0B5CFF] text-white font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
          data-testid="one-click-generate-btn"
        >
          {ocBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
          {ocBusy ? "Generating…" : "Generate 3 variants"}
        </button>
        {ocVariants && ocVariants.variants && (
          <div className="mt-2 space-y-1.5" data-testid="one-click-variants-strip">
            <div className="text-[10px] text-[#4A4D57]">Pick your favourite:</div>
            {ocVariants.variants.map((v) => (
              <button
                key={v.id}
                onClick={() => pickVariant(v)}
                disabled={ocPicking}
                data-testid={`ai-variant-${v.layout}`}
                className="w-full text-left px-2 py-1.5 rounded border border-[#E4E6EC] hover:border-[#7C3AED] text-[11px] disabled:opacity-50"
              >
                <div className="font-semibold text-[#0B0D17] truncate">{v.title}</div>
                <div className="text-[9px] text-[#747680] uppercase tracking-wider">{v.layout.replace(/-/g, " ")}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="text-xs font-semibold flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5 text-[#7C3AED]" /> AI Title Suggestions</div>
      <input className="vp-input text-xs" placeholder="Topic (e.g. crypto crash)" value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="ai-topic-input" />
      <button onClick={suggest} disabled={busy} className="vp-btn-primary text-xs py-1.5 px-3 w-full inline-flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="ai-suggest-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        Generate 5 titles
      </button>
      {titles.length > 0 && (
        <div className="space-y-1.5">
          {titles.map((t, i) => (
            <button key={i} onClick={() => applyTitle(t)} className="w-full text-left px-2 py-1.5 rounded border border-[#E4E6EC] hover:border-[#7C3AED] text-xs" data-testid={`ai-title-${i}`}>
              {t}
            </button>
          ))}
        </div>
      )}
      <div className="border-t border-[#E4E6EC] pt-3">
        <div className="text-xs font-semibold flex items-center gap-1.5"><Scissors className="w-3.5 h-3.5 text-[#7C3AED]" /> AI Remove Background</div>
        <div className="text-[10px] text-[#747680] mt-1">Provide an image URL below (or use current BG). AI will strip the background and add subject as a new layer.</div>
        <input className="vp-input text-xs mt-2" placeholder="https://image.url (optional)" value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} data-testid="ai-removebg-url" />
        <button onClick={removeBg} disabled={bgBusy} className="vp-btn-ghost text-xs py-1.5 px-3 w-full mt-2 inline-flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="ai-removebg-btn">
          {bgBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Scissors className="w-3.5 h-3.5" />}
          Remove background
        </button>
      </div>
    </div>
  );
}



// --------------------------------------------------------------
// MediaTab — drag-drop + file upload with gallery of previously uploaded images
// Stored in localStorage as data URLs (persists across sessions, no backend needed)
// --------------------------------------------------------------
function MediaTab({ addImage }) {
  const STORAGE_KEY = "vp_media_uploads";
  const [uploads, setUploads] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); } catch { return []; }
  });
  const [drag, setDrag] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const fileRef = useRef(null);

  const persist = (list) => {
    setUploads(list);
    try {
      // keep only last 40 to stay under storage quota
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, 40)));
    } catch { /* quota */ }
  };
  const acceptFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    const added = [];
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: too large (max 10MB)`); continue; }
      // eslint-disable-next-line no-await-in-loop
      const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      added.push({ id: Math.random().toString(36).slice(2, 10), url: dataUrl, name: f.name.slice(0, 40), created_at: Date.now() });
    }
    if (added.length) {
      persist([...added, ...uploads]);
      toast.success(`Uploaded ${added.length} image${added.length > 1 ? "s" : ""}`);
    }
  };
  const removeUpload = (id) => persist(uploads.filter((u) => u.id !== id));
  const addUrl = () => {
    const u = urlInput.trim();
    if (!/^https?:\/\//.test(u) && !u.startsWith("data:")) { toast.error("Enter a valid URL"); return; }
    persist([{ id: Math.random().toString(36).slice(2, 10), url: u, name: u.slice(-30), created_at: Date.now() }, ...uploads]);
    setUrlInput("");
    toast.success("Image saved");
  };

  return (
    <div className="space-y-2">
      {/* Drag-drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); acceptFiles(e.dataTransfer.files); }}
        onClick={() => fileRef.current?.click()}
        className={"cursor-pointer border-2 border-dashed rounded-lg p-4 text-center transition " +
                   (drag ? "border-[#0B5CFF] bg-[#0B5CFF]/5" : "border-[#E4E6EC] hover:border-[#0B5CFF]/50")}
        data-testid="media-dropzone"
      >
        <Upload className="w-6 h-6 mx-auto text-[#7C3AED] mb-1" />
        <div className="text-xs font-semibold">Drop images here or click</div>
        <div className="text-[10px] text-[#747680] mt-0.5">JPG / PNG / WEBP, up to 10MB each</div>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
               onChange={(e) => acceptFiles(e.target.files)} data-testid="media-file-input" />
      </div>
      {/* URL add */}
      <div className="flex gap-1">
        <input className="vp-input py-1 text-xs flex-1" placeholder="Paste image URL"
               value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") addUrl(); }} data-testid="media-url-input" />
        <button onClick={addUrl} className="vp-btn-ghost text-xs py-1 px-2" data-testid="media-url-add">Add</button>
      </div>
      {/* Gallery */}
      {uploads.length > 0 && (
        <div>
          <div className="text-[10px] text-[#747680] mb-1">Your uploads · click to add to canvas</div>
          <div className="grid grid-cols-3 gap-1.5">
            {uploads.map((u) => (
              <div key={u.id} className="relative group aspect-square rounded overflow-hidden border border-[#E4E6EC] hover:border-[#0B5CFF]">
                <button
                  onClick={() => { addImage(u.url); toast.success("Added to canvas"); }}
                  className="w-full h-full"
                  title={u.name}
                  data-testid={`media-item-${u.id}`}
                >
                  <img src={u.url} alt={u.name} className="w-full h-full object-cover" />
                </button>
                <button
                  onClick={() => removeUpload(u.id)}
                  className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 bg-black/60 hover:bg-[#B00020] text-white rounded p-0.5"
                  title="Remove"
                ><Trash2 className="w-2.5 h-2.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

